import { isEmpty } from 'lodash';
import type { Field } from '../api/Field';
import type { FilterEvaluator } from '../api/FilterEvaluator';
import { MatchNoneEvaluator } from '../api/MatchNoneEvaluator';
import type { Filter } from '../api/Filter';
import type { Value } from '../api/Value';
import type { ComparableValue } from '../api/ComparableValue';
import { LeafFilterBuilder } from './LeafFilterBuilder';

export class MaxLengthFilterBuilder extends LeafFilterBuilder {
  protected _buildEvaluator<RecordType>(
    filter: Filter,
    field: Field<RecordType>,
    values: object[],
  ): FilterEvaluator<RecordType> {
    if (isEmpty(values)) {
      return MatchNoneEvaluator.INSTANCE as FilterEvaluator<RecordType>;
    }
    const comparableValues = this.toComparableValues(field, values);
    return new MaxLengthEvaluator(field, comparableValues[0]);
  }
}

export class MaxLengthEvaluator<RecordType> implements FilterEvaluator<RecordType> {
  private field: Field<RecordType>;
  private readonly maxLength: ComparableValue<unknown>;

  constructor(field: Field<RecordType>, maxLength: ComparableValue<unknown>) {
    this.field = field;
    this.maxLength = maxLength;
  }

  public evaluate(record: RecordType): boolean {
    const iterable = this.field.getValues(record);
    if (iterable === null) {
      // we are considering it as an empty string
      const num = Number(this.maxLength.getValue());
      if (isNaN(num)) {
        return false;
      }
      return num >= 0;
    }
    for (const v of iterable) {
      if (this.evaluateValue(v)) {
        return true;
      }
    }
    return false;
  }

  private evaluateValue(v: Value): boolean {
    return v.maxLength(this.maxLength);
  }
}
